import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import SmsMarketingConsent from "./SmsMarketingConsent";
import { ThemeContext, LanguageContext, UserContext } from "../../contexts/Context";
import { IoCloseOutline, IoLogoInstagram } from "react-icons/io5";
import { getDobInputValue, validateRegistrationDob } from "../../utils/registrationDob";
import {
    getAssetUrl,
    getCompanyAddress,
    getCompanyPhone,
    getInstagramInfo,
    getOperatingHours
} from "./loginDisplayUtil";

const buildCustomerName = (customer) => {
    return [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim() || customer.customerName || "";
};



const CustomerRegister = ({ show, setShow, setShowError, setError, onSubmit, setShowPdpa, initialPhone = "", initialName = "" }) => {
    const [displayShow, setDisplay] = useState(show ? "flex" : "none");
    const themeContext = React.useContext(ThemeContext);
    const languageContext = React.useContext(LanguageContext);
    const {
        lanIndex, company, bgImg, landingImg, otherUrls, openingTime, closingTime,
        mustDob, hideOptionalField, mustEmail
    } = React.useContext(UserContext);
    const [customer, setCustomer] = useState({
        customerName: initialName,
        firstName: initialName,
        lastName: "",
        phone: initialPhone,
        email: "",
        dob: "",
        gender: 0,
        userPassword: "",
        smsMarketingConsent: false,
        pdpa: 0
    });
    const [passwordConfirm, setPasswordConfirm] = useState("");

    useEffect(() => {
        setDisplay(show ? "flex" : "none");
    }, [show]);

    const handleChange = (value, name) => {
        setCustomer((prevCustomer) => {
            const nextCustomer = {
                ...prevCustomer,
                [name]: value
            };

            if (name === "firstName" || name === "lastName") {
                nextCustomer.customerName = buildCustomerName(nextCustomer);
            }

            return nextCustomer;
        });
    };

    const handleSubmit = () => {
        const submitCustomer = {
            ...customer,
            customerName: buildCustomerName(customer)
        };

        if (submitCustomer.phone === "" || submitCustomer.phone.length < 7 || submitCustomer.phone.length > 15) {
            setError([{ errorMessage: "Phone no cannot be less than 7 or greater than 15" }]);
            return setShowError(true);
        } else if (submitCustomer.pdpa === 0 || submitCustomer.pdpa === "0") {
            setError([{ errorMessage: "Must agree the terms & condition" }]);
            return setShowError(true);
        } else if (!hideOptionalField && submitCustomer.userPassword !== passwordConfirm) {
            setError([{ errorMessage: "Password confirmation does not match" }]);
            return setShowError(true);
        } else if (mustEmail) {
            if (submitCustomer.email === "" || submitCustomer.email.includes("@") === false ||
                submitCustomer.email.includes(".") === false) {
                setError([{ errorMessage: "Invalid Email" }]);
                return setShowError(true);
            }
        }
        const dobResult = validateRegistrationDob(submitCustomer.dob, mustDob);
        if (dobResult.error) {
            setError([{ errorMessage: dobResult.error }]);
            return setShowError(true);
        }
        submitCustomer.dob = dobResult.value;

        onSubmit(submitCustomer);
    };

    const operatingHours = getOperatingHours(openingTime, closingTime);
    const companyAddress = getCompanyAddress(company);
    const companyPhone = getCompanyPhone(company);
    const instagramInfo = getInstagramInfo(otherUrls);
    return (
        <React.Fragment>
            <ModalContainer display={displayShow} bgImg={bgImg} themeContext={themeContext}>
                <DialogSurface bgImg={bgImg} themeContext={themeContext}>
                    <IconButton onClick={() => setShow(false)}>
                        <IoCloseOutline size={34} />
                    </IconButton>
                    <ContentWrapper>
                        <FormSection>
                        <ImgWrapper image={company && company.companyLogo} />
                        <TitleText>CREATE A NEW ACCOUNT</TitleText>
                        <Divider />
                        {!hideOptionalField &&
                            <React.Fragment>
                                <SectionTitle>PERSONAL INFORMATION</SectionTitle>
                                <GenderRow>
                                    <RadioLabel>
                                        <RadioInput
                                            name="gender"
                                            value={0}
                                            checked={`${customer.gender}` === "0"}
                                            type="radio"
                                            onChange={(e) => { handleChange(e.target.value, "gender") }}
                                        />
                                        Mr.
                                    </RadioLabel>
                                    <RadioLabel>
                                        <RadioInput
                                            name="gender"
                                            value={1}
                                            checked={`${customer.gender}` === "1"}
                                            type="radio"
                                            onChange={(e) => { handleChange(e.target.value, "gender") }}
                                        />
                                        Miss.
                                    </RadioLabel>
                                </GenderRow>
                                <Input
                                    value={customer.firstName}
                                    type="text"
                                    onChange={(e) => { handleChange(e.target.value, "firstName") }}
                                    placeholder="First Name*"
                                    aria-label="First Name"
                                />
                                <Input
                                    value={customer.lastName}
                                    type="text"
                                    onChange={(e) => { handleChange(e.target.value, "lastName") }}
                                    placeholder="Last Name*"
                                    aria-label="Last Name"
                                />
                            </React.Fragment>
                        }
                        <Input
                            value={customer.phone}
                            type="tel"
                            onChange={(e) => { handleChange(e.target.value, "phone") }}
                            placeholder={`${languageContext[lanIndex].phone}*`}
                            aria-label={languageContext[lanIndex].phone}
                        />
                        {(mustEmail || !hideOptionalField) &&
                            <Input
                                value={customer.email}
                                type="email"
                                onChange={(e) => { handleChange(e.target.value, "email") }}
                                placeholder={`${languageContext[lanIndex].email}${mustEmail ? "*" : ""}`}
                                aria-label={languageContext[lanIndex].email}
                            />
                        }
                        {!hideOptionalField &&
                            <React.Fragment>
                                <Input
                                    value={customer.userPassword}
                                    type="password"
                                    onChange={(e) => { handleChange(e.target.value, "userPassword") }}
                                    placeholder="Password*"
                                    aria-label="Password"
                                />
                                <Input
                                    value={passwordConfirm}
                                    type="password"
                                    onChange={(e) => { setPasswordConfirm(e.target.value) }}
                                    placeholder="Password Confirmation*"
                                    aria-label="Password Confirmation"
                                />
                            </React.Fragment>
                        }
                        {mustDob &&
                            <Input
                                value={getDobInputValue(customer && customer.dob)}
                                type="text"
                                onChange={(e) => { handleChange(e.target.value, "dob") }}
                                inputMode="numeric"
                                maxLength={8}
                                required={Boolean(mustDob)}
                                placeholder={`${languageContext[lanIndex].dob} (DDMMYYYY)`}
                                aria-label={languageContext[lanIndex].dob}
                            />
                        }
                        <SmsMarketingConsent
                            checked={customer && customer.smsMarketingConsent === true}
                            onChange={(checked) => handleChange(checked, "smsMarketingConsent")}
                        />
                        <TermsRow>
                            <TermsCheckbox
                                id="pdpa"
                                name="pdpa"
                                checked={customer.pdpa === 1 || customer.pdpa === "1"}
                                type="checkbox"
                                onChange={(e) => { handleChange(e.target.checked ? 1 : 0, "pdpa") }}
                            />
                            <TermsLabel htmlFor="pdpa">
                                {languageContext[lanIndex].agreePdpa}
                                <DetailLink onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowPdpa(true); }}>
                                    see detail
                                </DetailLink>
                            </TermsLabel>
                        </TermsRow>
                        <RegisterButton onClick={handleSubmit}>
                            CREATE ACCOUNT
                        </RegisterButton>
                        </FormSection>
                        {(landingImg || operatingHours || companyPhone || companyAddress || instagramInfo) &&
                            <FooterInfo>
                                {landingImg && <FooterLandingImage image={landingImg} />}
                                {operatingHours && <FooterLine>{operatingHours}</FooterLine>}
                                {companyPhone && <FooterLine>{companyPhone}</FooterLine>}
                                {companyAddress && <FooterAddress>{companyAddress}</FooterAddress>}
                                {instagramInfo && instagramInfo.url &&
                                    <InstagramLink href={instagramInfo.url} target="_blank" rel="noopener noreferrer">
                                        <IoLogoInstagram size={46} />
                                        {instagramInfo.handle && <InstagramHandle>{instagramInfo.handle.split("?")[0]}</InstagramHandle>}
                                    </InstagramLink>
                                }
                            </FooterInfo>
                        }
                    </ContentWrapper>
                </DialogSurface>
            </ModalContainer>
        </React.Fragment>
    );
};

export default CustomerRegister;

const DialogSurface = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    min-height: 100%;
    flex-direction: column;

    @media (min-width: 768px) {
        width: min(620px, 100%);
        min-height: 0;
        max-height: calc(100dvh - 48px);
        background-color: ${(props) => props.themeContext.backgroundColor};
        background-image: ${(props) => props.bgImg ? `url(${getAssetUrl(props.bgImg)})` : "none"};
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        border: 1px solid rgba(255, 255, 255, 0.72);
        border-radius: 26px;
        box-shadow: 0 30px 90px rgba(15, 23, 42, 0.34);
        overflow: hidden;
        animation: registerDialogIn 220ms cubic-bezier(0.22, 1, 0.36, 1);

        @keyframes registerDialogIn {
            from {
                opacity: 0;
                transform: translateY(18px) scale(0.98);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }
    }
`;

const RegisterButton = styled.div`
    display: flex;
    width: 68%;
    max-width: 360px;
    min-height: 62px;
    margin: 14px auto 0;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    font-weight: 400;
    border-radius: 34px;
    background-color: #5f5f5f;
    color: #ffffff;
    box-shadow: 0px 2px 5px rgba(0,0,0,0.18);
    cursor: pointer;

    @media (max-height: 760px) {
        min-height: 54px;
        font-size: 20px;
    }
`;

const Input = styled.input`
    width: 100%;
    min-height: 64px;
    border-radius: 12px;
    padding: 0 20px;
    font-size: 20px;
    border: 1px solid #9d9d9d;
    margin-bottom: 12px;
    background-color: rgba(255,255,255,0.94);
    color: #343434;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: #a9a9a9;
    }

    @media (max-height: 760px) {
        min-height: 56px;
        font-size: 18px;
        margin-bottom: 10px;
    }
`;

const ImgWrapper = styled.div`
    width: 220px;
    max-width: 78%;
    height: 110px;
    margin: 0 0 20px;
    background-image: ${(props) => props.image ? `url(${getAssetUrl(props.image)})` : "none"};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    @media (max-height: 760px) {
        width: 180px;
        height: 88px;
        margin-bottom: 14px;
    }
`;

const IconButton = styled.div`
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: 99;
    color: #555555;
    cursor: pointer;
`;

const TitleText = styled.div`
    color: #333333;
    font-size: 28px;
    font-weight: 300;
    line-height: 1;
    text-align: center;

    @media (max-height: 760px) {
        font-size: 24px;
    }
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    margin: 24px 0 20px;
    background-color: rgba(80,80,80,0.45);
`;

const SectionTitle = styled.div`
    align-self: flex-start;
    margin-bottom: 24px;
    font-size: 17px;
    font-weight: 700;
    color: #444444;
    letter-spacing: 0;
`;

const GenderRow = styled.div`
    display: flex;
    align-self: flex-start;
    gap: 18px;
    margin-bottom: 18px;
`;

const RadioLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 18px;
    color: #444444;
`;

const RadioInput = styled.input`
    width: 22px;
    height: 22px;
    margin: 0;
`;

const TermsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin: 6px 0 2px;
`;

const TermsCheckbox = styled.input`
    width: 24px;
    height: 24px;
    margin: 0;
`;

const TermsLabel = styled.label`
    font-size: 16px;
    font-weight: 600;
    color: #444444;
`;

const DetailLink = styled.small`
    margin-left: 8px;
    color: #555555;
    font-size: 12px;
    font-weight: 400;
    text-decoration: underline;
    cursor: pointer;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100dvh;
    box-sizing: border-box;
    padding: 44px 32px 34px;
    overflow-y: auto;
    gap: 34px;

    @media (max-height: 760px) {
        padding-top: 28px;
        gap: 24px;
    }

    @media (min-width: 768px) {
        min-height: 0;
        padding: 46px 42px 38px;
        scrollbar-width: thin;
    }
`;

const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 500px;
    padding-top: 16px;
`;

const FooterInfo = styled.div`
    width: 100%;
    max-width: 520px;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #555555;
    text-align: center;
`;

const FooterLandingImage = styled.div`
    width: 80%;
    height: 100px;
    margin-top: 100px;
    background-image: ${(props) => props.image ? `url(${getAssetUrl(props.image)})` : "none"};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;

    @media (max-height: 760px) {
        height: 72px;
    }
`;

const FooterLine = styled.div`
    margin-top: 6px;
    font-size: 21px;
    line-height: 1.15;
`;

const FooterAddress = styled.div`
    max-width: 430px;
    margin-top: 10px;
    font-size: 15px;
    font-style: italic;
    line-height: 1.25;
`;

const InstagramLink = styled.a`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-top: 42px;
    color: #555555;
    text-decoration: none;

    @media (max-height: 760px) {
        margin-top: 24px;
    }
`;

const InstagramHandle = styled.div`
    font-family: "contralto-xsmall", sans-serif;
    font-size: 14px;
`;

const ModalContainer = styled.div`
    display: ${(props) => props.display};
    font-family: "contralto-xsmall", sans-serif;
    position: fixed;
    flex-direction: column;
    background-color: ${(props) => props.themeContext.backgroundColor};
    background-image: ${(props) => props.bgImg ? `url(${getAssetUrl(props.bgImg)})` : "none"};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    top: 0;
    left: max(0px, calc((100vw - 550px) / 2));

    width: 100vw;
    max-width: 550px;
    height: 100vh;
    height: 100dvh;
    z-index: 1000;

    *, input, button, textarea, select {
        font-family: "contralto-xsmall", sans-serif !important;
    }

    animation: modalOrder .1s;
    animation-timing-function: ease-out;

    @media (min-width: 768px) {
        left: 0;
        width: 100vw;
        max-width: none;
        padding: 24px;
        box-sizing: border-box;
        justify-content: center;
        align-items: center;
        background-color: rgba(15, 23, 42, 0.58);
        background-image: none;
        backdrop-filter: blur(6px);
        z-index: 1000;
        animation: registerBackdropIn 180ms ease-out;

        @keyframes registerBackdropIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
    }

    @keyframes modalOrder {
        0%  {
        transform: translateY(350px);
        opacity: 0;
        }

        100%  {
        transform: translateY(0px);
        opacity: 1;
        }
    }
`;
